import type { InvoiceDetailResponse, InvoicePart } from '../../../types/invoice';
import { isCreditCardMiscFeeLine } from '../../../utils/invoicePaySettlement';
import { CALCULATION_MISMATCH_TOLERANCE } from './invoiceDetailFormPartMath';
import { isCorePartForArithmetic } from './invoiceDetailFormLineClassification';

export type LocalLineArithmeticIssue = {
    partIndex: number;
    partNumber: string;
    issueType: 'calculation_mismatch';
    issueDetails: {
        invoiceQty?: number;
        invoiceUnitPrice?: number;
        invoiceAmount?: number;
        calculatedAmount?: number;
    };
};

export function computeLocalLineArithmeticIssues(args: {
    editedFields: Partial<InvoiceDetailResponse>;
    invoice: InvoiceDetailResponse | undefined;
    parseDraftNumeric: (cellKey: string, fallback: number) => number;
}): LocalLineArithmeticIssue[] {
    const { editedFields, invoice, parseDraftNumeric } = args;
    const issues: LocalLineArithmeticIssue[] = [];

    const supplierText = editedFields.supplierText ?? invoice?.supplierText ?? null;

    const pushTab = (rows: InvoicePart[], baseIndex: number, keyPrefix: string) => {
        rows.forEach((item, i) => {
            const index = baseIndex + i;
            if (!item || isCorePartForArithmetic(item, supplierText)) return;
            const qty = typeof item.qty === 'number' ? item.qty : 0;
            const unitCommitted = typeof item.unitBuyPrice === 'number' ? item.unitBuyPrice : 0;
            const totalCommitted = typeof item.totalBuyPrice === 'number' ? item.totalBuyPrice : 0;
            const unit = parseDraftNumeric(`${keyPrefix}-${i}-unitBuyPrice`, unitCommitted);
            const total = parseDraftNumeric(`${keyPrefix}-${i}-totalBuyPrice`, totalCommitted);
            const calc = qty * unit;
            if (Math.abs(calc - total) > CALCULATION_MISMATCH_TOLERANCE) {
                issues.push({
                    partIndex: index,
                    partNumber: item.partNumber || '',
                    issueType: 'calculation_mismatch',
                    issueDetails: { invoiceQty: qty, invoiceUnitPrice: unit, invoiceAmount: total, calculatedAmount: calc },
                });
            }
        });
    };

    const lineItems = (editedFields.lineItems ?? invoice?.lineItems ?? []) as InvoicePart[];
    const freightItems = (editedFields.freightItems ?? invoice?.freightItems ?? []) as InvoicePart[];
    const fuelItems = (editedFields.fuelItems ?? invoice?.fuelItems ?? []) as InvoicePart[];
    const restockingFeeItems = (editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? []) as InvoicePart[];
    const skipCcMiscForCharge = (editedFields.paySettlementType ?? invoice?.paySettlementType) === 'charge';

    let offset = 0;
    pushTab(lineItems, offset, 'lineItem');
    offset += lineItems.length;
    pushTab(freightItems, offset, 'freightItem');
    offset += freightItems.length;
    pushTab(fuelItems, offset, 'fuelItem');
    offset += fuelItems.length;

    restockingFeeItems.forEach((item, i) => {
        if (skipCcMiscForCharge && isCreditCardMiscFeeLine(item)) return;
        const index = offset + i;
        if (!item || isCorePartForArithmetic(item, supplierText)) return;
        const qty = typeof item.qty === 'number' ? item.qty : 0;
        const unitCommitted = typeof item.unitBuyPrice === 'number' ? item.unitBuyPrice : 0;
        const totalCommitted = typeof item.totalBuyPrice === 'number' ? item.totalBuyPrice : 0;
        const unit = parseDraftNumeric(`restockingFeeItem-${i}-unitBuyPrice`, unitCommitted);
        const total = parseDraftNumeric(`restockingFeeItem-${i}-totalBuyPrice`, totalCommitted);
        const calc = qty * unit;
        if (Math.abs(calc - total) > CALCULATION_MISMATCH_TOLERANCE) {
            issues.push({
                partIndex: index,
                partNumber: item.partNumber || '',
                issueType: 'calculation_mismatch',
                issueDetails: { invoiceQty: qty, invoiceUnitPrice: unit, invoiceAmount: total, calculatedAmount: calc },
            });
        }
    });

    return issues;
}
